import { select } from '@clack/prompts'
import { Command } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { teamSchema } from '@scalar-org/entities/team'
import { namespaceSchema, slugSchema } from '@scalar-org/entities/utility'

import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const UpdateSchemaCommand = () => {
  const cmd = new Command('update')

  cmd.description('Update schema metadata.')

  cmd.option('-s, --slug <slug>', 'Schema slug')
  cmd.option('-n, --namespace <namespace>', 'Team namespace')

  cmd.option('--title <title>', 'Title')
  cmd.option('--description <description>', 'Description')
  cmd.option('--isPrivate <isPrivate>', 'Privacy (public/private)')

  cmd.action(async (opts) => {
    const { error, data } = z
      .object({
        slug: slugSchema.optional(),
        namespace: namespaceSchema.optional(),
        title: z.string().max(50).optional(),
        description: z.string().optional(),
        isPrivate: z.boolean().optional(),
      })
      .safeParse(opts)

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const db = await getDatabaseClient()

    const client = await apiService()

    const team = await db.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    /**
     * Most teams will have 1 namespace, so we default to that.
     * Ideally we should also have a global cli namespace selected.
     */
    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your schema. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const schemas = await client.managedSchemas.getSchemas(namespace)

    if (schemas.error)
      return output.error().title(schemas.message).exit('error')

    const schemaSlug =
      schemas.data.find((schema) => schema.slug === data.slug)?.slug ||
      ((await select({
        message: 'Please select a schema to update',
        options: schemas.data.map((schema) => ({
          value: schema.slug,
          label: schema.slug,
        })),
      })) as string)

    if (!schemaSlug) {
      return output
        .error()
        .title('No schema found')
        .message('Please provide a valid schema slug')
        .exit('error')
    }

    const logger = output.info().loader('Updating the schema')

    const response = await client.managedSchemas.update(namespace, schemaSlug, {
      title: data.title,
      description: data.description,
      isPrivate: data.isPrivate,
    })

    if (response.error) {
      return output
        .error()
        .title('Something went wrong')
        .message(response.message)
        .exit('error')
    }

    logger
      .update('loader', 0, { content: 'Schema updated successfully!' })
      .exit('success')
  })

  return cmd
}
